import React, { useState } from 'react';
import { FileText, PencilLine } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';

interface StepProps {
  result: {
    ui_step: string;
    signal_name: string;
    suggestions: string[];
  };
  onSelect: (value: string, label: string) => void;
}

const DescriptionSelectorStep: React.FC<StepProps> = ({ result, onSelect }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [isCustom, setIsCustom] = useState(false);
  const [customText, setCustomText] = useState('');

  const handleSelect = (description: string) => {
    setSelected(description);
    onSelect(description, description);
  };

  const handleCustomSubmit = () => {
    if (!customText.trim()) return;
    setSelected(customText);
    onSelect(customText, customText);
  };

  return (
    <div className="w-full max-w-lg">
      <p className="mb-1 text-sm font-medium text-gray-700">
        Describe your signal
      </p>
      <p className="mb-3 text-xs text-gray-500">
        Pick a description for <span className="font-medium text-gray-700">"{result.signal_name}"</span>
      </p>

      <div className="space-y-2">
        {(result.suggestions || []).map((suggestion, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleSelect(suggestion)}
            disabled={selected !== null}
            className={cn(
              'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all',
              selected === suggestion
                ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500'
                : selected !== null
                  ? 'border-gray-200 bg-white opacity-50 cursor-not-allowed'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 cursor-pointer',
            )}
          >
            <div
              className={cn(
                'mt-0.5 rounded-md p-1.5',
                selected === suggestion ? 'bg-violet-100 text-violet-600' : 'bg-gray-100 text-gray-500',
              )}
            >
              <FileText className="h-4 w-4" />
            </div>
            <p className="text-sm text-gray-700">{suggestion}</p>
          </button>
        ))}

        {!isCustom && selected === null && (
          <button
            type="button"
            onClick={() => setIsCustom(true)}
            className="flex w-full items-center gap-2 rounded-lg border border-dashed border-gray-300 p-3 text-left text-sm text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-700"
          >
            <PencilLine className="h-4 w-4" />
            Write my own
          </button>
        )}

        {isCustom && selected === null && (
          <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
            <input
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCustomSubmit();
                }
              }}
              placeholder="Type your description..."
              autoFocus
              className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsCustom(false)}
                className="rounded-md px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCustomSubmit}
                disabled={!customText.trim()}
                className="rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-violet-700 disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DescriptionSelectorStep;
